/**
 * "Память" — the memory-debt block on the home screen (FEAT-023, addendum §1).
 *
 * This is a discipline indicator, so it is **always visible**, zeros included.
 * Hiding it when the numbers are clean would remove exactly the signal that
 * says the habit is being kept; a debt block you only see when you are already
 * in debt teaches nothing. Zeros render in `--hint`, non-zero in `--dstr`.
 *
 * Never a gate: nothing here blocks work, and there is no nagging copy.
 */

import { Brain, CircleDashed, FileText, Lightbulb } from "lucide-react";

import { Memory } from "@/api/resources";
import type { MemoryStats } from "@/api/types";
import { Cell, CellGroup } from "@/components/ui/Cell";
import { Section } from "@/components/ui/Section";
import { SkeletonBlock } from "@/components/ui/Skeleton";
import { cn } from "@/lib/cn";
import { pluralRu } from "@/lib/format";
import { useQuery } from "@tanstack/react-query";

/** §1 of the addendum — 0 is muted, anything above it is a debt. */
function DebtValue({ n }: { n: number }) {
  return (
    <span className={cn("text-cell font-medium tabular-nums", n > 0 ? "text-dstr" : "text-hint")}>
      {n}
    </span>
  );
}

function CountValue({ n }: { n: number }) {
  return (
    <span className={cn("text-cell font-medium tabular-nums", n > 0 ? "text-accent" : "text-hint")}>
      {n}
    </span>
  );
}

export function MemoryBlock() {
  const stats = useQuery({ queryKey: ["memory-stats"], queryFn: Memory.stats });

  if (stats.isLoading) {
    return (
      <Section title="Память">
        <SkeletonBlock className="h-[136px]" />
      </Section>
    );
  }

  // A failed debt query must not take the home screen with it: the numbers are
  // context for deciding what to do, not a precondition for doing it.
  if (stats.isError || !stats.data) return null;

  const s: MemoryStats = stats.data;

  return (
    <Section
      title="Память"
      trailing={<Brain className="h-[18px] w-[18px] text-hint" aria-hidden="true" />}
    >
      <CellGroup inset="icon">
        <Cell
          icon={<FileText className="h-5 w-5" aria-hidden="true" />}
          title="Закрыто без журнала"
          subtitle="за 30 дней"
          trailing={<DebtValue n={s.closed_without_journal_30d} />}
        />
        {/*
          FIX-027 P4 — GET /tasks/stale now exists, so this row leads somewhere.
          The chevron appears only when there is something to open: a tap target
          that lands on an empty list is worse than a plain figure.
        */}
        <Cell
          icon={<CircleDashed className="h-5 w-5" aria-hidden="true" />}
          title="Без записей больше недели"
          subtitle={`${s.stale_in_progress} ${pluralRu(s.stale_in_progress, "задача", "задачи", "задач")} в работе молчит`}
          to={s.stale_in_progress > 0 ? "/tasks/stale" : undefined}
          chevron={s.stale_in_progress > 0}
          trailing={<DebtValue n={s.stale_in_progress} />}
        />
        <Cell
          icon={<Lightbulb className="h-5 w-5" aria-hidden="true" />}
          title="Идеи ждут твоего решения"
          to={s.ideas_awaiting_my_review > 0 ? "/ideas?filter=review" : undefined}
          trailing={<CountValue n={s.ideas_awaiting_my_review} />}
          chevron={s.ideas_awaiting_my_review > 0}
        />
      </CellGroup>
      {s.weekly.entries > 0 && (
        <p className="px-3 text-meta text-hint">
          За неделю {s.weekly.entries}{" "}
          {pluralRu(s.weekly.entries, "запись", "записи", "записей")} в журнале
          {s.weekly.top_tools.length > 0 && (
            <> · чаще всего {s.weekly.top_tools.map((t) => t.slug).join(", ")}</>
          )}
        </p>
      )}
    </Section>
  );
}
